/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';

import { concatMapTo, map, mergeMap, shareReplay, switchMap, take } from 'rxjs/operators';

import { FeatureSelection, GeometryCategory, IsolationQueryResults, MapQueryPropertyFilter } from '../../types';
import { MapConfigService } from '../../modules/map/services/mapConfig.service';
import { RestService } from '../../modules/shared/services/rest.service';
import { MapService } from '../../modules/map/services/map.service';
import { QueryService } from '../../modules/map/services/query.service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { AppConfigState } from '../../core/state/appConfig/app-config.state';
import { LoggerService } from '../../modules/shared/services/logger.service';
import { MergeLayerDisplayFeatures, SetLayerDisplayFeatures } from '../../modules/map/state/active-map.actions';
import { DisplayFeaturePayload } from '../../modules/map/state/active-map.model';
import { ClearSelection, MergeSelection, SetSelection } from '../../modules/map/state/selection.actions';
import { FeatureService } from '../../modules/map/services/feature.service';

@Injectable({providedIn: 'root'})
export class LineIsolationService {
    private isolationDataInternal: IsolationQueryResults;
    private mapName: string;
    private layerName: string;
    private unsubscriber: Subscription;

    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private mapConfig: MapConfigService,
        private mapService: MapService,
        private queryService: QueryService,
        private featureService: FeatureService,
        private actions$: Actions,
        private store: Store
    ) {}

    public query(mapName: string, layerName: string, feature: any) {
        const url = `isolation/query/${mapName}/${layerName}/${feature}`;

        const lineLayer = this.mapConfig.getLayer(layerName);
        const nodeLayer = this.mapConfig.getLayer(this.isolationLayers[mapName][layerName].nodeLayer);

        const source = this.mapService.getVectorLayer('isolation').getSource();

        const query$ = this.rest.apiGet(url, null).pipe(
            map((queryData: IsolationQueryResults) => {
                this.mapName = mapName;
                this.layerName = layerName;
                this.isolationDataInternal = queryData;

                // get rid of the current selection
                this.queryService.clearSelection();
                return queryData;
            }),
            shareReplay()
        );

        const lineQuery$ = query$.pipe(
            mergeMap((_: any) => {
                const lineFilter: MapQueryPropertyFilter = {
                    filter: {
                        property: this.isolationLayers[mapName][layerName].lineFeatureField.name,
                        values: this.isolationData.lines,
                    },
                    type: 'property',
                };

                return this.queryService.queryLayers$([lineLayer], [], lineFilter, source, false, false, true);
            }),
            switchMap((lineFeatures) => {
                const featureIds = lineFeatures.reduce(
                    (acc: string[], featureObject) => acc.concat.apply(acc, featureObject.featureIds),
                    []
                );
                const styles = this.mapConfig.getStyles('isolation', GeometryCategory.Line);
                this.featureService.updateFeatureStyle(featureIds, styles);
                const payload: DisplayFeaturePayload = {
                    displayLayer: 'isolation',
                    featureIds,
                };
                return this.store.dispatch(new MergeLayerDisplayFeatures(payload));
            })
        );

        const nodeQuery$ = query$.pipe(
            mergeMap((_: any) => {
                const nodeFilter: MapQueryPropertyFilter = {
                    filter: {
                        property: this.isolationLayers[mapName][layerName].nodeFeatureField.name,
                        values: this.isolationData.nodes,
                    },
                    type: 'property',
                };

                return this.queryService.queryLayers$([nodeLayer], [], nodeFilter, source, false, false, true);
            }),
            switchMap((nodeFeatures) => {
                const featureIds = nodeFeatures.reduce(
                    (acc: string[], featureObject) => acc.concat.apply(acc, featureObject.featureIds),
                    []
                );
                const styles = this.mapConfig.getStyles('isolation', GeometryCategory.Point);
                this.featureService.updateFeatureStyle(featureIds, styles);
                const payload: DisplayFeaturePayload = {
                    displayLayer: 'isolation',
                    featureIds,
                };
                return this.store.dispatch(new MergeLayerDisplayFeatures(payload));
            })
        );

        this.unsubscriber = forkJoin([lineQuery$, nodeQuery$])
            .pipe(concatMapTo(this.actions$), ofActionSuccessful(ClearSelection, SetSelection, MergeSelection), take(1))
            .subscribe(() => this.clearIsolationData());
    }

    public selectFeatures() {
        // we're going to make a selection based on isolation data, so remove the
        // action handlers
        this.unsubscriber.unsubscribe();

        // get rid of the current selection
        this.queryService.clearSelection();

        const lineFilter: MapQueryPropertyFilter = {
            filter: {
                property: this.isolationLayers[this.mapName][this.layerName].lineFeatureField.name,
                values: this.isolationDataInternal.lines,
            },
            type: 'property',
        };
        const lineQuery$ = this.queryService.doQuery(lineFilter, true, false, this.layerName);

        const nodeFilter: MapQueryPropertyFilter = {
            filter: {
                property: this.isolationLayers[this.mapName][this.layerName].nodeFeatureField.name,
                values: this.isolationDataInternal.nodes,
            },
            type: 'property',
        };
        const nodeQuery$ = this.queryService.doQuery(
            nodeFilter,
            true,
            false,
            this.isolationLayers[this.mapName][this.layerName].nodeLayer
        );
        return forkJoin([lineQuery$, nodeQuery$]);
    }

    public getSelectedFeature(mapName, layerName) {
        const layerIsolationConfig = this.isolationLayers[mapName][layerName];
        return this.queryService
            .getSingleSelection$(layerName)
            .pipe(
                map((selection: FeatureSelection) =>
                    selection ? selection.fields[layerIsolationConfig.lineFeatureField.name] : null
                )
            );
    }

    public isIsolationEnabled(mapName: string, layerName: string): Observable<boolean> {
        return this.queryService.getSelection(layerName).pipe(
            map((selection) => {
                let enabled = false;
                if (this.isolationLayers) {
                    enabled =
                        !!this.isolationLayers[mapName] &&
                        !!this.isolationLayers[mapName][layerName] &&
                        selection.length === 1;
                }
                return enabled;
            })
        );
    }

    public clearIsolationData() {
        this.mapName = null;
        this.layerName = null;
        this.isolationDataInternal = null;
        // this.mapService.getVectorLayer('isolation').getSource().clear();
        this.store.dispatch(
            new SetLayerDisplayFeatures({
                displayLayer: 'isolation',
                featureIds: [],
            })
        );
    }

    get hasIsolationData() {
        return !!this.isolationDataInternal;
    }

    get isolationLayers() {
        return this.store.selectSnapshot(AppConfigState.isolation);
    }

    get isolationData() {
        return this.isolationDataInternal;
    }
}
